import clsx from 'clsx';
import { Field, FormikProvider, useFormik } from 'formik';
import loadScript from 'load-script2';
import React, { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import { CalendarIcon, ClockIcon } from 'lucide-react';
import { useMarketo } from '../../../contexts/marketo-context';
import { useFormValidation } from '../../../hooks/form-validation';
import { useMarketoFormSubmit } from '../../../hooks/marketo';
import useIsMobile from '../../../hooks/mobile';
import { getCookie, sendTrackingData, sleep } from '../../../utils';
import {
	validateEmail,
	validateJobTitle,
	validatePhoneNumber,
	validateString,
} from '../../../utils/marketo';
import TickIcon from '../../../assets/tick.inline.svg';
import { Button } from '../../atoms/button';
import { CallUs } from '../../atoms/call-us';
import { SidebarHeader } from '../../atoms/sidebar-header';
import { TextInput } from '../../atoms/text-input';
import { getLocale } from '../../../utils/locale';
import CountdownTimer from '../../glu/molecules/countdown-timer';
import useCountdown from '../../../hooks/useCountdown';
import usePrivacyLink from '../../../hooks/usePrivacyLink';
import { PBSLink } from '../../atoms/link';

const GLUExitPopUp = () => {
	const [showPopUp, setShowPopUp] = useState(false);
	// eslint-disable-next-line no-unused-vars
	const [loading, setLoading] = useState(false);
	const [showSuccessMessage, setShowSuccessMessage] = useState(false);
	const [technicalError, setTechnicalError] = useState(false);
	const formId = 4925;
	const isMobile = useIsMobile();
	const [referralCookie, setReferralCookie] = useState('');
	const [keywordCookie, setKeywordCookie] = useState('');
	const [mediumCookie, setMediumCookie] = useState('');
	const [campaignCookie, setCampaignCookie] = useState('');
	const [msclkidCookie, setMsclkidCookie] = useState('');
	const [msclkidLastCookie, setMsclkidLastCookie] = useState('');
	const [gclidCookie, setGclidCookie] = useState('');
	const [gclidLastCookie, setGclidLastCookie] = useState('');
	const [campaignLastCookie, setCampaignLastCookie] = useState('');
	const [contentLastCookie, setContentLastCookie] = useState('');
	const [mediumLastCookie, setMediumLastCookie] = useState('');
	const [sourceLastCookie, setSourceLastCookie] = useState('');
	const [termLastCookie, setTermLastCookie] = useState('');
	const [googleCookie, setGoogleCookie] = useState('');
	const url = useLocation();
	const urlLocale = getLocale(url.pathname);
	const { privacyLink } = usePrivacyLink(urlLocale);

	const [windowWidth, setWindowWidth] = useState(
		typeof window !== 'undefined' ? window.innerWidth : 1200
	);
	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		if (windowWidth < 1024) {
			const timer = setTimeout(() => {
				if (!sessionStorage.getItem('exitPopUpShown')) {
					setShowPopUp(true);
					sessionStorage.setItem('exitPopUpShown', 'true');
				}
			}, 15000);
			return () => clearTimeout(timer);
		}
	}, [windowWidth]);

	useEffect(() => {
		if (windowWidth < 1024) return;
		if (isMobile) return;

		const handleMouseLeave = (e) => {
			if (
				(e.clientX <= 0 || e.clientY <= 0) &&
				!sessionStorage.getItem('exitPopUpShown')
			) {
				setShowPopUp(true);
				sessionStorage.setItem('exitPopUpShown', 'true');
			}
		};

		window.addEventListener('mouseout', handleMouseLeave);

		return () => {
			window.removeEventListener('mouseout', handleMouseLeave);
		};
	}, [isMobile, windowWidth]);

	const handleClose = () => {
		setShowPopUp(false);
		setShowSuccessMessage(false);
		setTechnicalError(false);
	};

	const MktoForms2Instances = useMarketo();
	const initialValidations = {
		FirstName: { isValid: true },
		isClient: { isValid: true },
		CanNumber: { isValid: true },
		LastName: { isValid: true },
		Email: { isValid: true },
		Phone: { isValid: true },
		Company: { isValid: true },
		JobTitle: { isValid: true },
		discuss: { isValid: true },
	};
	// Fetch webinar data using GraphQL query
	const data = useStaticQuery(graphql`
		query WebinarData {
			datoCmsGluSiteSetting(locale: { eq: "en" }) {
				upcomingWebinar {
					title
					dateAndTime
					timeEnd
				}
			}
		}
	`);

	const webinarDateAndTime =
		data?.datoCmsGluSiteSetting?.upcomingWebinar?.dateAndTime;

	const timeEnd = data?.datoCmsGluSiteSetting?.upcomingWebinar?.timeEnd;

	const webinarTitle = data?.datoCmsGluSiteSetting?.upcomingWebinar?.title;
	const date = format(new Date(webinarDateAndTime), 'MMM d, yyyy');
	const time = `${format(new Date(webinarDateAndTime), 'h:mm')} - ${timeEnd}`;

	const firstNameRef = useRef(null);
	const lastNameRef = useRef(null);
	const companyRef = useRef(null);
	const emailRef = useRef(null);
	const phoneNumberRef = useRef(null);
	const jobTitleRef = useRef(null);
	const { validations, validateForm } = useFormValidation(initialValidations);

	useEffect(() => {
		if (!validations.FirstName.isValid && firstNameRef.current) {
			return firstNameRef.current.focus();
		}

		if (!validations.LastName.isValid && lastNameRef.current) {
			return lastNameRef.current.focus();
		}

		if (!validations.Email.isValid && emailRef.current) {
			return emailRef.current.focus();
		}

		if (validations.Phone.isValid === false && phoneNumberRef.current) {
			return phoneNumberRef.current.focus();
		}

		if (!validations.Company.isValid && companyRef.current) {
			return companyRef.current.focus();
		}

		if (!validations.JobTitle.isValid && jobTitleRef.current) {
			return jobTitleRef.current.focus();
		}
	}, [validations]);

	const initialValues = {
		FirstName: '',
		LastName: '',
		Email: '',
		Phone: '',
		Company: '',
		Title: '',
		CanNumber: '',
		discuss: '',
		PersonSource:
			urlLocale === 'ie'
				? 'Website - ROI Confirm Webinar Web Lead'
				: 'Website - Confirm Webinar Web Lead',
		LeadSource:
			urlLocale === 'ie'
				? 'Website - ROI Confirm Webinar Web Lead'
				: 'Website - Confirm Webinar Web Lead',
		referalSource: referralCookie,
		keyword: keywordCookie,
		utm_medium: mediumCookie,
		utm_campaign: campaignCookie,
		MSCLKID__c: msclkidCookie,
		MSCLKID_last: msclkidLastCookie,
		GCLID__c: gclidCookie,
		gCLID_last: gclidLastCookie,
		utm_campaign_last__c: campaignLastCookie,
		utm_content_last__c: contentLastCookie,
		utm_medium_last__c: mediumLastCookie,
		utm_source_last__c: sourceLastCookie,
		utm_term_last__c: termLastCookie,
		Google_Cookie_ID__c: googleCookie,
	};

	const formik = useFormik({
		initialValues,
		enableReinitialize: true,
		validateOnChange: false,
		validateOnBlur: false,
	});

	const handleClickTracking = () => {
		const gtmStartedEvent = window.dataLayer?.find(
			(element) => element['gtm.start']
		);
		if (!gtmStartedEvent) return;

		sendTrackingData('exit_popup', {
			form_name: 'Exit Pop Up',
		});
	};

	const submitHandler = async (event) => {
		setLoading(true);
		event.preventDefault();
		formik.setSubmitting(true);

		const { formEntries, isValid } = validateForm(event.currentTarget);
		if (!isValid) {
			formik.setSubmitting(false);
			setLoading(false);
			return;
		}

		const payloadData = {
			...initialValues,
			FirstName: formEntries.FirstName,
			LastName: formEntries.LastName,
			Phone: formEntries.Phone,
			Email: formEntries.Email,
			Company: formEntries.Company,
			Title: formEntries.JobTitle,
			PersonSource:
				urlLocale === 'ie'
					? 'Website - ROI Confirm Webinar Web Lead'
					: 'Website - Confirm Webinar Web Lead',
		};

		try {
			await sleep(500);
			await useMarketoFormSubmit(
				payloadData,
				formId,
				MktoForms2Instances
			);
			formik.setStatus('submitted');
			setShowSuccessMessage(true);
			setTechnicalError(false);
		} catch (error) {
			formik.setStatus('error');
			setTechnicalError(true);
			setShowSuccessMessage(false);
		} finally {
			formik.setSubmitting(false); // End submission state
			handleClickTracking();
		}
	};
	useEffect(() => {
		if (isMobile) return;

		const loadForm = () =>
			loadScript(
				'https://app-lon04.marketo.com/js/forms2/js/forms2.min.js'
			)
				.then(() => {
					MktoForms2Instances.get(
						'https://app-lon04.marketo.com'
					).loadForm(
						'https://app-lon04.marketo.com',
						'023-IMK-845',
						formId
					);
				})
				.catch(() => {});

		if (window.requestIdleCallback) {
			window.requestIdleCallback(loadForm);
		} else {
			setTimeout(loadForm);
		}

		setReferralCookie(getCookie('utm_source'));
		setKeywordCookie(getCookie('utm_term'));
		setMediumCookie(getCookie('utm_medium'));
		setCampaignCookie(getCookie('utm_campaign'));
		setMsclkidCookie(getCookie('msclkid'));
		setMsclkidLastCookie(getCookie('msclkid_last'));
		setGclidCookie(getCookie('gclid'));
		setGclidLastCookie(getCookie('gclid_last'));
		setCampaignLastCookie(getCookie('utm_campaign_last'));
		setContentLastCookie(getCookie('utm_content_last'));
		setMediumLastCookie(getCookie('utm_medium_last'));
		setSourceLastCookie(getCookie('utm_source_last'));
		setTermLastCookie(getCookie('utm_term_last'));
		setGoogleCookie(getCookie('Google_Cookie_ID__c'));
	}, [MktoForms2Instances, formId, isMobile]);

	const validateEmailPage = (value) => {
		let error;
		const emailAddressRegex =
			/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/i;

		if (!value) {
			error = false;
		} else if (!emailAddressRegex.test(value)) {
			error = false;
		} else {
			error = true;
		}
		return error;
	};
	// eslint-disable-next-line no-unused-vars
	const isEmailValid = validateEmailPage(emailRef?.current?.value);

	const { hasExpired } = useCountdown(webinarDateAndTime);

	return (
		<>
			{showPopUp && !hasExpired && (
				<div
					className={clsx(
						'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50',
						windowWidth < 1024
							? 'items-start h-auto min-h-screen'
							: 'items-center'
					)}
				>
					<div
						className={clsx(
							'flex flex-row justify-center mx-auto',
							windowWidth < 1024
								? 'w-full max-w-full'
								: 'glulgx:w-[70%] max-w-m-screen glu2xl:w-[30%]'
						)}
					>
						<div
							className={clsx(
								'bg-white flex-1 flex flex-col relative rounded-sm',
								windowWidth < 1024
									? 'w-full h-full max-w-full overflow-y-auto max-h-screen'
									: ''
							)}
						>
							<Button
								type="button"
								variant="Unset"
								onClick={handleClose}
								className="absolute top-2  right-2 mr-2 mt-2 text-black bg-white hover:bg-white hover:text-black rounded-lg hover:text-gray-700"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth={1.5}
									stroke="currentColor"
									className="w-6 h-6"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</Button>

							{technicalError && (
								<div className="px-4 pb-10 bg-white">
									<SidebarHeader
										heading="Oops - your submission didn't quite go through"
										text="Don't worry though -  just drop us an email with your details and we'll get your spot secured for you"
										ariaLive="assertive"
									/>
									<div className="flex justify-between items-center">
										<CallUs className="p-4" />
										<a
											href="mailto:e-learning@peninsula-uk.com"
											className="mt-4 text-brand-blue-400 font-bold break-all underline"
										>
											e-learning@peninsula-uk.com
										</a>
									</div>
								</div>
							)}
							{!technicalError && showSuccessMessage && (
								<>
									<div className="flex flex-col overflow-hidden rounded-sm">
										<h3 className="w-full p-4 text-2xl text-white bg-brand-blue-400 lg:p-8">
											<TickIcon
												className="inline-block w-5 h-auto mr-4 text-white shrink-0"
												aria-hidden="true"
											/>{' '}
											Your spot has been secured
										</h3>
										<div className="p-4 bg-white lg:p-8">
											<p className="mb-5 font-bold">
												Thank you for registering your
												spot to our webinar{' '}
												<span className="text-brand-red-400">
													{webinarTitle}
												</span>
											</p>
											<p>
												A reminder of the start date has
												been sent out to your registered
												email with the details of the
												webinar.
											</p>
											<p className="text-xl text-center font-lexend-regular text-blue-500 pt-5">
												For more upcoming live webinars,{' '}
												<a
													href="/elearning/webinars"
													className="text-brand-red-400 underline"
												>
													click here
												</a>
												.
											</p>
										</div>
									</div>
								</>
							)}

							{!technicalError && !showSuccessMessage && (
								<div className="bg-brand-blue-400 text-white p-7 pb-1 rounded-t-sm">
									<h2 className="text-2xl lg:mb-5 mb-0">
										Upcoming{' '}
										<span className="text-brand-red-500">
											live session
										</span>
									</h2>
									<h2 className=" font-bold text-lg">
										<span className="text-brand-red-500 text-2xl">
											Webinar -
										</span>{' '}
										{webinarTitle}
										<div className="flex ">
											<p className=" text-xl">
												<CalendarIcon
													className=" text-blue-300 inline-block mr-2 "
													aria-hidden
												/>
												{date}{' '}
												<ClockIcon
													className=" text-blue-300 inline-block"
													aria-hidden
												/>{' '}
												{time}
											</p>
										</div>
									</h2>
									{!isMobile && (
										<CountdownTimer
											targetDate={webinarDateAndTime}
										/>
									)}
								</div>
							)}

							{!technicalError && !showSuccessMessage && (
								<div className="p-6">
									<FormikProvider value={formik}>
										<form
											onSubmit={submitHandler}
											data-formid={formId}
											data-forminstance="one"
										>
											<div className="flex flex-row gap-6">
												<Field
													name="FirstName"
													validate={validateString}
												>
													{({ field }) => (
														<div className="w-full lg:w-1/2">
															<label
																htmlFor="FirstName"
																className="text-brand-blue-400 lg:text-base-f font-lexend-medium"
															>
																First name&#42;
															</label>
															<TextInput
																id="FirstName"
																name="FirstName"
																aria-required="true"
																className="w-full mb-3 wrapper-small"
																ref={
																	firstNameRef
																}
																showValidation={
																	!validations
																		.FirstName
																		.isValid &&
																	!firstNameRef
																		.current
																		.value
																}
																validationMessage={
																	!validations
																		.FirstName
																		.isValid
																		? 'Please enter your first name'
																		: null
																}
																{...field}
																space={false}
																placeholder={`${
																	urlLocale ===
																	'ie'
																		? 'Sean'
																		: 'Jane'
																}`}
																validateOnBlur
															/>
														</div>
													)}
												</Field>
												<Field
													name="LastName"
													validate={validateString}
												>
													{({ field }) => (
														<div className="w-full lg:w-1/2">
															<label
																htmlFor="LastName"
																className="text-brand-blue-400 lg:text-base-f font-lexend-medium"
															>
																Last name&#42;
															</label>
															<TextInput
																id="LastName"
																name="LastName"
																aria-required="true"
																className="w-full mb-3 wrapper-small"
																ref={
																	lastNameRef
																}
																showValidation={
																	!validations
																		.LastName
																		.isValid &&
																	!lastNameRef
																		.current
																		.value
																}
																validationMessage={
																	!validations
																		.LastName
																		.isValid
																		? 'Please enter your last name'
																		: null
																}
																{...field}
																space={false}
																placeholder={`${
																	urlLocale ===
																	'ie'
																		? 'Murphy'
																		: 'Smith'
																}`}
																validateOnBlur
															/>
														</div>
													)}
												</Field>
											</div>
											<div className="flex flex-row gap-6">
												<Field
													name="Company"
													validate={validateString}
												>
													{({ field }) => (
														<div className="w-full lg:w-1/2">
															<label
																htmlFor="Company"
																className="text-brand-blue-400 lg:text-base-f font-lexend-medium"
															>
																Company&#42;
															</label>
															<TextInput
																id="Company"
																name="Company"
																aria-required="true"
																className="w-full mb-3 wrapper-small"
																ref={companyRef}
																showValidation={
																	!validations
																		.Company
																		.isValid &&
																	!companyRef
																		.current
																		.value
																}
																validationMessage={
																	!validations
																		.Company
																		.isValid
																		? 'Please enter your company'
																		: null
																}
																{...field}
																space={false}
																placeholder="Company LTD"
																validateOnBlur
															/>
														</div>
													)}
												</Field>
												<Field
													name="JobTitle"
													validate={validateJobTitle}
												>
													{({ field }) => (
														<div className="w-full lg:w-1/2">
															<label
																htmlFor="JobTitle"
																className="text-brand-blue-400 lg:text-base-f font-lexend-medium"
															>
																Job title&#42;
															</label>
															<TextInput
																id="JobTitle"
																name="JobTitle"
																aria-required="true"
																className="w-full mb-3 wrapper-small"
																ref={
																	jobTitleRef
																}
																showValidation={
																	!validations
																		.JobTitle
																		.isValid
																}
																validationMessage={
																	!validations
																		.JobTitle
																		.isValid
																		? 'Please enter at least 2 characters'
																		: null
																}
																{...field}
																space={false}
																placeholder="HR Manager"
																validateOnBlur
															/>
														</div>
													)}
												</Field>
											</div>

											<div className="flex flex-row gap-6">
												<Field
													name="Email"
													validate={validateEmail}
												>
													{({ field }) => (
														<div className="w-full lg:w-1/2">
															<label
																htmlFor="Email"
																className="text-brand-blue-400 lg:text-base-f font-lexend-medium"
															>
																Email&#42;
															</label>
															<TextInput
																id="Email"
																type="email"
																name="Email"
																aria-required="true"
																className="w-full mb-3 wrapper-small"
																ref={emailRef}
																showValidation={
																	!validations
																		.Email
																		.isValid &&
																	!emailRef
																		.current
																		.value
																}
																validationMessage="Please enter your email address"
																{...field}
																space={false}
																placeholder={`${
																	urlLocale ===
																	'ie'
																		? 'sean.murphy@gmail.com'
																		: 'jane.smith@gmail.com'
																}`}
																validateOnBlur
															/>
														</div>
													)}
												</Field>
												<Field
													name="Phone"
													validate={
														validatePhoneNumber
													}
												>
													{({ field }) => (
														<div className="w-full lg:w-1/2">
															<label
																htmlFor="Phone"
																className="text-brand-blue-400 lg:text-base-f font-lexend-medium"
															>
																Phone&#42;
															</label>
															<TextInput
																id="Phone"
																name="Phone"
																aria-required="true"
																className="w-full mb-3 wrapper-small"
																ref={
																	phoneNumberRef
																}
																showValidation={
																	!validations
																		.Phone
																		.isValid ===
																		true &&
																	phoneNumberRef.current
																}
																validationMessage="Please enter a phone number"
																{...field}
																space={false}
																placeholder={
																	urlLocale ===
																	'ie'
																		? '087 1234 567'
																		: '07997 210 007'
																}
																type="tel"
																validateOnBlur
															/>
														</div>
													)}
												</Field>
											</div>

											<div className="flex flex-col pt-2 pb-4 bg-white  lg:flex-row justify-between">
												<div className="wrapper-small">
													<Button
														type="submit"
														className={clsx(
															formik.isSubmitting
																? 'justify-center loading'
																: null
														)}
														disabled={hasExpired}
													>
														<span>
															{formik.isSubmitting
																? 'Please wait...'
																: 'Reserve your spot'}
														</span>
														{formik.isSubmitting && (
															<span
																className="sr-only"
																aria-live="assertive"
															>
																Submitting your
																details, please
																wait...
															</span>
														)}
													</Button>
												</div>
												<p
													className={clsx(
														'pt-3 text-center bg-white lg:text-base-f',
														windowWidth < 1024
															? 'w-full max-w-full overflow-y-auto '
															: ''
													)}
												>
													View our{' '}
													<PBSLink
														to={privacyLink}
														variant="Link"
														size="Small"
													>
														privacy notice
													</PBSLink>
													.
												</p>
											</div>
										</form>
									</FormikProvider>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default GLUExitPopUp;

GLUExitPopUp.defaultProps = {
	locale: 'en',
};

GLUExitPopUp.propTypes = {
	locale: PropTypes.string,
};
